'use client'

import { cn } from '@/lib/utils'
import { useEffect, useState } from 'react'
import { MARKETING_ENGINES } from '@/lib/marketing/engines'

const AI_PLATFORMS = MARKETING_ENGINES.map((e) => ({ src: e.logoSrc, name: e.displayName }))

const CYCLE_MS = 2000
const TRANSITION_MS = 400

type AiPlatformCycleProps = {
  iconSize?: string
  innerGap?: string
  className?: string
}

export function AiPlatformCycle({
  iconSize = 'size-4',
  innerGap = 'gap-1.5',
  className,
}: AiPlatformCycleProps) {
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
    let interval: ReturnType<typeof setInterval> | undefined

    const start = () => {
      interval = setInterval(() => {
        setVisible(false)
        setTimeout(() => {
          setIndex((prev) => (prev + 1) % AI_PLATFORMS.length)
          setVisible(true)
        }, TRANSITION_MS)
      }, CYCLE_MS)
    }

    // React to the OS setting changing mid-session. setState lives only in this
    // async listener (never synchronously in the effect body).
    const onChange = () => {
      clearInterval(interval)
      if (mql.matches) {
        setIndex(0)
        setVisible(true)
      } else {
        start()
      }
    }

    // First run: only start the cycle if motion is allowed (initial state is
    // already the frozen first frame, so no synchronous setState is needed).
    if (!mql.matches) start()
    mql.addEventListener('change', onChange)
    return () => {
      mql.removeEventListener('change', onChange)
      clearInterval(interval)
    }
  }, [])

  return (
    <span className={cn('inline-grid', className ? className : '')}>
      {AI_PLATFORMS.map((platform, i) => {
        const active = i === index && visible
        return (
          <span
            key={platform.name}
            className={`col-start-1 row-start-1 inline-flex items-center ${innerGap} transition-all`}
            style={{
              transitionDuration: `${TRANSITION_MS}ms`,
              opacity: active ? 1 : 0,
              filter: active ? 'blur(0px)' : 'blur(4px)',
            }}
          >
            {/* Decorative cycling logo — screen readers get the full sentence via sr-only in H1 */}
            {/* eslint-disable-next-line @next/next/no-img-element -- <img> required: next/image adds wrapper divs that break the opacity/blur CSS animation */}
            <img
              src={platform.src}
              alt=""
              aria-hidden="true"
              className={`${iconSize} dark:brightness-0 dark:invert`}
            />
          </span>
        )
      })}
    </span>
  )
}
